import { log, logE, logV } from "../../shared/services/debug-logger";
import type {
  HoldCleaningAssessment,
  HoldCleaningAssessmentAction,
  HoldCleaningAssessmentEvidence,
  HoldCleaningAssessmentResult,
  HoldCleaningReadiness,
} from "../models/hold-cleaning.models";
import {
  generateHoldCleaningAssessment,
  getHoldCleaningErrorMessage,
  recordHoldCleaningAiUsage,
  saveHoldCleaningAssessment,
  submitHoldCleaningAiFeedback,
} from "../services/hold-cleaning.service";
import { HoldCleaningPanel, HoldCleaningReadinessBadge } from "./HoldCleaningUi";
import { HandThumbDownIcon, HandThumbUpIcon, PlusIcon, SparklesIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";

const fieldClass =
  "mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#007d6e] focus:outline-none focus:ring-1 focus:ring-[#007d6e] disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100";
const primaryButton =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[#007d6e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#00675b] disabled:cursor-not-allowed disabled:opacity-50";
const secondaryButton =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700";

const validateAssessment = (assessment: HoldCleaningAssessment): string[] => {
  const errors: string[] = [];
  if (!assessment.payload.summary.trim()) errors.push("Add a summary before saving.");
  if (assessment.payload.summary.length > 10_000) errors.push("Assessment summary cannot exceed 10,000 characters.");
  if (assessment.payload.actions.length > 100) errors.push("No more than 100 actions are allowed.");
  assessment.payload.actions.forEach((action, index) => {
    if (!action.action.trim()) errors.push(`Action ${index + 1} requires a description.`);
    if (action.action.length > 1_000) errors.push(`Action ${index + 1} exceeds 1,000 characters.`);
    if ((action.owner?.length ?? 0) > 320) errors.push(`Action ${index + 1} owner exceeds 320 characters.`);
    if ((action.dueAt?.length ?? 0) > 64) errors.push(`Action ${index + 1} due date exceeds 64 characters.`);
  });
  if (assessment.payload.evidence.length > 200) errors.push("No more than 200 evidence references are allowed.");
  assessment.payload.evidence.forEach((evidence, index) => {
    if (!evidence.source.trim()) errors.push(`Evidence ${index + 1} requires a source.`);
    if (!evidence.referenceId.trim()) errors.push(`Evidence ${index + 1} requires a reference.`);
    if (evidence.source.length > 100) errors.push(`Evidence ${index + 1} source exceeds 100 characters.`);
    if (evidence.referenceId.length > 300) errors.push(`Evidence ${index + 1} reference exceeds 300 characters.`);
    if ((evidence.note?.length ?? 0) > 1_000) errors.push(`Evidence ${index + 1} note exceeds 1,000 characters.`);
  });
  if ((assessment.payload.imageIds?.length ?? 0) > 500) errors.push("No more than 500 assessment images are allowed.");
  return errors;
};

export const HoldCleaningAssessmentPanel = ({
  voyageId,
  initial,
  imageIds,
  canUseAi,
  canEdit,
}: {
  voyageId: string;
  initial: HoldCleaningAssessmentResult;
  imageIds: string[];
  canUseAi: boolean;
  canEdit: boolean;
}) => {
  const [assessment, setAssessment] = useState(initial.assessment);
  const [etag, setEtag] = useState(initial.etag);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [draftPreview, setDraftPreview] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState("");
  const validationErrors = useMemo(() => validateAssessment(assessment), [assessment]);

  useEffect(() => {
    setAssessment(initial.assessment);
    setEtag(initial.etag);
    setValidationAttempted(false);
    setDraftPreview(false);
  }, [initial]);

  const updatePayload = <K extends keyof HoldCleaningAssessment["payload"]>(
    key: K,
    value: HoldCleaningAssessment["payload"][K],
  ) => {
    setAssessment((current) => ({ ...current, payload: { ...current.payload, [key]: value } }));
  };

  const updateAction = (index: number, patch: Partial<HoldCleaningAssessmentAction>) => {
    updatePayload(
      "actions",
      assessment.payload.actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, ...patch } : action,
      ),
    );
  };

  const updateEvidence = (index: number, patch: Partial<HoldCleaningAssessmentEvidence>) => {
    updatePayload(
      "evidence",
      assessment.payload.evidence.map((evidence, evidenceIndex) =>
        evidenceIndex === index ? { ...evidence, ...patch } : evidence,
      ),
    );
  };

  const save = async () => {
    log("HoldCleaningAssessmentPanel - save", { validationErrors: validationErrors.length });
    if (validationErrors.length > 0) {
      setValidationAttempted(true);
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await saveHoldCleaningAssessment(voyageId, assessment.payload, etag);
      setAssessment(result.assessment);
      setEtag(result.etag);
      setValidationAttempted(false);
      setDraftPreview(false);
      log("STATE - save - assessment applied", { revision: result.assessment.revision });
      setNotice("Assessment saved.");
    } catch (requestError) {
      logE(new Error("HoldCleaningAssessmentPanel - save - failed"));
      setError(getHoldCleaningErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    log("HoldCleaningAssessmentPanel - generate", { images: imageIds.length });
    setGenerating(true);
    setError(null);
    setNotice(null);
    try {
      const result = await generateHoldCleaningAssessment(
        voyageId,
        imageIds,
        assessment.revision > 0 ? assessment.revision : undefined,
      );
      setAssessment(result.assessment);
      setEtag(result.etag);
      setValidationAttempted(false);
      log("STATE - generate - draft applied", { revision: result.assessment.revision });
      setDraftPreview(true);
      setNotice("AI draft ready. Review and save it if you want to keep it.");
    } catch (requestError) {
      logE(new Error("HoldCleaningAssessmentPanel - generate - failed"));
      setError(getHoldCleaningErrorMessage(requestError));
    } finally {
      setGenerating(false);
    }
  };

  const sendFeedback = async (rating: "POSITIVE" | "NEGATIVE") => {
    log("HoldCleaningAssessmentPanel - sendFeedback", { rating });
    if (!assessment.assessmentId) return;
    setError(null);
    setNotice(null);
    try {
      await submitHoldCleaningAiFeedback({
        assessmentId: assessment.assessmentId,
        revisionNumber: assessment.revision,
        rating,
        comment: feedbackComment.trim() || undefined,
      });
      await recordHoldCleaningAiUsage(
        rating === "POSITIVE" ? "AI_ASSESSMENT_ACCEPTED" : "AI_ASSESSMENT_DISMISSED",
        voyageId,
        assessment.assessmentId,
      );
      setFeedbackComment("");
      setNotice("Feedback recorded.");
    } catch (requestError) {
      logE(new Error("HoldCleaningAssessmentPanel - sendFeedback - failed"));
      setError(getHoldCleaningErrorMessage(requestError));
    }
  };

  const busy = saving || generating;
  logV("HoldCleaningAssessmentPanel - BUILD", { busy, canEdit, canUseAi });

  return (
    <HoldCleaningPanel
      title="Hold cleaning assessment"
      action={
        <div className="flex items-center gap-2">
          <HoldCleaningReadinessBadge readiness={assessment.payload.readiness} />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {assessment.revision > 0 ? `Revision ${assessment.revision}` : "New assessment"}
          </span>
        </div>
      }
    >
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">Review this assessment before relying on it.</p>
      {!canEdit && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
          You have read-only assessment access.
        </div>
      )}
      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950">
          {notice}
        </div>
      )}
      {canEdit && validationAttempted && validationErrors.length > 0 && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        >
          {validationErrors.length === 1 ? (
            validationErrors[0]
          ) : (
            <ul className="list-disc space-y-1 pl-5">
              {validationErrors.map((validationError) => (
                <li key={validationError}>{validationError}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
        Readiness
        <select
          className={fieldClass}
          value={assessment.payload.readiness}
          onChange={(event) => updatePayload("readiness", event.target.value as HoldCleaningReadiness)}
          disabled={!canEdit || busy}
        >
          <option value="UNASSESSED">Unassessed</option>
          <option value="READY">Ready</option>
          <option value="READY_WITH_ACTIONS">Ready with actions</option>
          <option value="NOT_READY">Not ready</option>
        </select>
      </label>
      <label className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-200">
        Assessment summary
        <textarea
          className={fieldClass}
          rows={5}
          value={assessment.payload.summary}
          onChange={(event) => updatePayload("summary", event.target.value)}
          maxLength={10_000}
          disabled={!canEdit || busy}
        />
      </label>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Required actions
          </h3>
          {canEdit && (
            <button
              type="button"
              className={secondaryButton}
              disabled={busy || assessment.payload.actions.length >= 100}
              onClick={() => updatePayload("actions", [...assessment.payload.actions, { action: "" }])}
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Add action
            </button>
          )}
        </div>
        {assessment.payload.actions.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">No required actions.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {assessment.payload.actions.map((action, index) => (
              <div
                key={index}
                className="grid gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700 md:grid-cols-6"
              >
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 md:col-span-3">
                  Action
                  <input
                    className={fieldClass}
                    value={action.action}
                    onChange={(event) => updateAction(index, { action: event.target.value })}
                    maxLength={1_000}
                    disabled={!canEdit || busy}
                  />
                </label>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Owner
                  <input
                    className={fieldClass}
                    value={action.owner ?? ""}
                    onChange={(event) => updateAction(index, { owner: event.target.value || undefined })}
                    maxLength={320}
                    disabled={!canEdit || busy}
                  />
                </label>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Due
                  <input
                    type="date"
                    className={fieldClass}
                    value={action.dueAt?.slice(0, 10) ?? ""}
                    onChange={(event) => updateAction(index, { dueAt: event.target.value || undefined })}
                    disabled={!canEdit || busy}
                  />
                </label>
                {canEdit && (
                  <button
                    type="button"
                    className="self-end rounded-lg border border-red-200 p-2 text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300"
                    aria-label={`Remove action ${index + 1}`}
                    disabled={busy}
                    onClick={() =>
                      updatePayload(
                        "actions",
                        assessment.payload.actions.filter((_, actionIndex) => actionIndex !== index),
                      )
                    }
                  >
                    <TrashIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Evidence references
          </h3>
          {canEdit && (
            <button
              type="button"
              className={secondaryButton}
              disabled={busy || assessment.payload.evidence.length >= 200}
              onClick={() =>
                updatePayload("evidence", [...assessment.payload.evidence, { source: "", referenceId: "" }])
              }
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Add evidence
            </button>
          )}
        </div>
        {assessment.payload.evidence.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">No assessment evidence is linked.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {assessment.payload.evidence.map((evidence, index) => (
              <div
                key={index}
                className="grid gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700 md:grid-cols-6"
              >
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Source
                  <input
                    className={fieldClass}
                    value={evidence.source}
                    onChange={(event) => updateEvidence(index, { source: event.target.value })}
                    maxLength={100}
                    disabled={!canEdit || busy}
                  />
                </label>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 md:col-span-2">
                  Reference
                  <input
                    className={fieldClass}
                    value={evidence.referenceId}
                    onChange={(event) => updateEvidence(index, { referenceId: event.target.value })}
                    maxLength={300}
                    disabled={!canEdit || busy}
                  />
                </label>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 md:col-span-2">
                  Note
                  <input
                    className={fieldClass}
                    value={evidence.note ?? ""}
                    onChange={(event) => updateEvidence(index, { note: event.target.value || undefined })}
                    maxLength={1_000}
                    disabled={!canEdit || busy}
                  />
                </label>
                {canEdit && (
                  <button
                    type="button"
                    className="self-end rounded-lg border border-red-200 p-2 text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300"
                    aria-label={`Remove evidence ${index + 1}`}
                    disabled={busy}
                    onClick={() =>
                      updatePayload(
                        "evidence",
                        assessment.payload.evidence.filter((_, evidenceIndex) => evidenceIndex !== index),
                      )
                    }
                  >
                    <TrashIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <button type="button" className={primaryButton} onClick={save} disabled={busy}>
              {saving ? "Saving…" : "Save assessment"}
            </button>
          )}
          {canUseAi && (
            <button type="button" className={secondaryButton} onClick={generate} disabled={busy}>
              <SparklesIcon className="h-4 w-4" aria-hidden="true" />
              {generating ? "Generating…" : "Generate AI draft"}
            </button>
          )}
        </div>
        {canUseAi && !draftPreview && assessment.source === "AI" && assessment.assessmentId && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              value={feedbackComment}
              onChange={(event) => setFeedbackComment(event.target.value)}
              maxLength={2_000}
              placeholder="Reason or validation note"
              aria-label="Assessment validation note"
            />
            <button type="button" className={secondaryButton} onClick={() => sendFeedback("POSITIVE")}>
              <HandThumbUpIcon className="h-4 w-4" aria-hidden="true" />
              Confirm assessment
            </button>
            <button type="button" className={secondaryButton} onClick={() => sendFeedback("NEGATIVE")}>
              <HandThumbDownIcon className="h-4 w-4" aria-hidden="true" />
              Disagree - add reason
            </button>
          </div>
        )}
      </div>
    </HoldCleaningPanel>
  );
};
